import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import { Today, ChevronLeft, ChevronRight } from '@mui/icons-material';
import CrewClearanceChips from './CrewClearanceChips';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { utcToDisplayDateAndTime } from '../../../../utils/timezoneUtils';

/** Blocked row that links to a duty roster entry (flight duty or scheduled off) */
function isOpenableDutyBlock(bp) {
  return (bp.type === 'DUTY' || bp.type === 'SCHEDULED_DUTY_OFF') && bp.details?.id;
}

/** API day shape from /calendar/availability */
function calendarDayKind(dayAvail) {
  if (!dayAvail) return 'unknown';
  if (dayAvail.isFullyUnavailable) return 'unavailable';
  if (dayAvail.isRosterPlanWorkOnlyDay === true) return 'rosterWork';
  if (dayAvail.isFullyAvailable) return 'available';
  return 'partial';
}

const ROSTER_WORK_CALENDAR = {
  bg: '#e0f2f1',
  border: '#00897b',
  chipLabel: 'Rostered to work'
};

/**
 * Calendar View component for Crew Duty Roster
 */
const CrewDutyRosterCalendar = ({
  calendarAvailability,
  calendarLoading,
  calendarStartDate,
  setCalendarStartDate,
  calendarEndDate,
  setCalendarEndDate,
  calendarViewType,
  setCalendarViewType,
  calendarDisplayMode,
  setCalendarDisplayMode,
  selectedCrewFilter,
  setSelectedCrewFilter,
  crewMembers,
  handleViewDutyFromCalendar,
  displayTimezone
}) => {
  const formatTime = (iso) => {
    if (!iso) return '—';
    if (displayTimezone) {
      const { time } = utcToDisplayDateAndTime(iso, displayTimezone);
      return time || dayjs(iso).format('HH:mm');
    }
    return dayjs(iso).format('HH:mm');
  };
  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">Crew Availability Calendar</Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Date Range Pickers */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={calendarStartDate}
              onChange={(newValue) => {
                setCalendarStartDate(newValue);
                if (calendarViewType === 'week') {
                  setCalendarEndDate(newValue.endOf('week'));
                } else if (calendarViewType === 'month') {
                  setCalendarEndDate(newValue.endOf('month'));
                } else {
                  setCalendarEndDate(newValue);
                }
              }}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
            />
            <DatePicker
              label="End Date"
              value={calendarEndDate}
              onChange={(newValue) => setCalendarEndDate(newValue)}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
            />
          </LocalizationProvider>

          {/* View Type Selector */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>View Type</InputLabel>
            <Select
              value={calendarViewType}
              onChange={(e) => {
                const viewType = e.target.value;
                setCalendarViewType(viewType);
                if (viewType === 'week') {
                  setCalendarEndDate(calendarStartDate.endOf('week'));
                } else if (viewType === 'month') {
                  setCalendarEndDate(calendarStartDate.endOf('month'));
                } else {
                  setCalendarEndDate(calendarStartDate);
                }
              }}
              label="View Type"
            >
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="month">Month</MenuItem>
            </Select>
          </FormControl>

          {/* Display Mode Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={calendarDisplayMode === 'grid'}
                onChange={(e) => setCalendarDisplayMode(e.target.checked ? 'grid' : 'calendar')}
              />
            }
            label={calendarDisplayMode === 'grid' ? 'Grid View' : 'Calendar View'}
          />

          {/* Crew Filter */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Crew Filter</InputLabel>
            <Select
              value={selectedCrewFilter}
              onChange={(e) => setSelectedCrewFilter(e.target.value)}
              label="Crew Filter"
            >
              <MenuItem value="">All Crew</MenuItem>
              {crewMembers.map((crew) => (
                <MenuItem key={crew._id} value={crew._id}>
                  {crew.firstName} {crew.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<Today />}
            onClick={() => {
              const today = dayjs();
              setCalendarStartDate(today.startOf('week'));
              setCalendarEndDate(today.endOf('week'));
              setCalendarViewType('week');
            }}
            size="small"
          >
            Today
          </Button>

          <IconButton
            onClick={() => {
              const days = calendarViewType === 'week' ? 7 : calendarViewType === 'month' ? 30 : 1;
              const newStart = calendarStartDate.subtract(days, 'day');
              const newEnd = calendarViewType === 'week' 
                ? newStart.endOf('week')
                : calendarViewType === 'month'
                ? newStart.endOf('month')
                : newStart;
              setCalendarStartDate(newStart);
              setCalendarEndDate(newEnd);
            }}
            size="small"
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={() => {
              const days = calendarViewType === 'week' ? 7 : calendarViewType === 'month' ? 30 : 1;
              const newStart = calendarStartDate.add(days, 'day');
              const newEnd = calendarViewType === 'week' 
                ? newStart.endOf('week')
                : calendarViewType === 'month'
                ? newStart.endOf('month')
                : newStart;
              setCalendarStartDate(newStart);
              setCalendarEndDate(newEnd);
            }}
            size="small"
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      {calendarLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography>Loading calendar data...</Typography>
        </Box>
      ) : calendarDisplayMode === 'grid' ? (
        /* Grid View - Crew names as rows, dates as columns */
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 200, position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'white' }}>
                  <strong>Crew Member</strong>
                </TableCell>
                {(() => {
                  const dates = [];
                  let currentDate = calendarStartDate;
                  while (currentDate.isBefore(calendarEndDate) || currentDate.isSame(calendarEndDate, 'day')) {
                    dates.push(currentDate);
                    currentDate = currentDate.add(1, 'day');
                  }
                  return dates.map((date) => (
                    <TableCell key={date.format('YYYY-MM-DD')} align="center" sx={{ minWidth: 150 }}>
                      <Typography variant="caption" display="block">
                        {date.format('ddd')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {date.format('DD MMM')}
                      </Typography>
                    </TableCell>
                  ));
                })()}
              </TableRow>
            </TableHead>
            <TableBody>
              {calendarAvailability
                .filter(item => !selectedCrewFilter || item.crew.id === selectedCrewFilter)
                .map((item) => {
                  const dates = [];
                  let currentDate = calendarStartDate;
                  while (currentDate.isBefore(calendarEndDate) || currentDate.isSame(calendarEndDate, 'day')) {
                    dates.push(currentDate);
                    currentDate = currentDate.add(1, 'day');
                  }
                  return (
                    <TableRow key={item.crew.id}>
                      <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, backgroundColor: 'white', fontWeight: 'medium' }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <span>
                            {item.crew.firstName} {item.crew.lastName}
                          </span>
                          <CrewClearanceChips crew={item.crew} />
                        </Stack>
                      </TableCell>
                      {dates.map((date) => {
                        const dayAvailability = item.availability.find(
                          a => a.date === date.format('YYYY-MM-DD')
                        );
                        if (!dayAvailability) {
                          return (
                            <TableCell key={date.format('YYYY-MM-DD')} align="center">
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={date.format('YYYY-MM-DD')} align="center" sx={{ p: 0.5 }}>
                            <Tooltip
                              title={
                                <Box>
                                  {calendarDayKind(dayAvailability) === 'rosterWork' && (
                                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                      <strong>Roster plan:</strong> scheduled work duty. Pilot remains assignable for charters
                                      / sectors; this is not the same as a booked flight duty.
                                    </Typography>
                                  )}
                                  {dayAvailability.hasRosterPlanWork && dayAvailability.rosterPlanWorkPeriods?.length > 0 && (
                                    <Box sx={{ mb: 1 }}>
                                      <Typography variant="caption" display="block">
                                        <strong>Roster work (plan):</strong>
                                      </Typography>
                                      {dayAvailability.rosterPlanWorkPeriods.map((rp, idx) => (
                                        <Typography key={idx} variant="caption" display="block">
                                          {formatTime(rp.start)} - {formatTime(rp.end)}
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                  <Typography variant="caption" display="block">
                                    <strong>Available Slots:</strong> {dayAvailability.availableSlots.length}
                                  </Typography>
                                  {dayAvailability.availableSlots.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                      {dayAvailability.availableSlots.map((slot, idx) => (
                                        <Typography key={idx} variant="caption" display="block">
                                          {formatTime(slot.start)} - {formatTime(slot.end)} ({slot.duration.toFixed(1)}h)
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                  {dayAvailability.blockedPeriods.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" display="block">
                                        <strong>Blocked:</strong>
                                      </Typography>
                                      {dayAvailability.blockedPeriods.map((blocked, idx) => (
                                        <Typography key={idx} variant="caption" display="block">
                                          {blocked.type}: {formatTime(blocked.start)} - {formatTime(blocked.end)}
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              }
                            >
                                <Box
                                onClick={() => {
                                  const rpId = dayAvailability.rosterPlanWorkPeriods?.[0]?.dutyId;
                                  if (dayAvailability.isRosterPlanWorkOnlyDay && rpId) {
                                    handleViewDutyFromCalendar(rpId);
                                    return;
                                  }
                                  const dutyBlock = dayAvailability.blockedPeriods.find(isOpenableDutyBlock);
                                  if (dutyBlock && dutyBlock.details.id) {
                                    handleViewDutyFromCalendar(dutyBlock.details.id);
                                  }
                                }}
                                sx={{
                                  p: 1,
                                  borderRadius: 1,
                                  minHeight: 60,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5,
                                  backgroundColor:
                                    calendarDayKind(dayAvailability) === 'rosterWork'
                                      ? ROSTER_WORK_CALENDAR.bg
                                      : dayAvailability.isFullyAvailable
                                        ? '#e8f5e9'
                                        : dayAvailability.isFullyUnavailable
                                          ? '#ffebee'
                                          : '#fff3e0',
                                  border: `2px solid ${
                                    calendarDayKind(dayAvailability) === 'rosterWork'
                                      ? ROSTER_WORK_CALENDAR.border
                                      : dayAvailability.isFullyAvailable
                                        ? '#4caf50'
                                        : dayAvailability.isFullyUnavailable
                                          ? '#f44336'
                                          : '#ff9800'
                                  }`,
                                  cursor:
                                    (dayAvailability.isRosterPlanWorkOnlyDay &&
                                      dayAvailability.rosterPlanWorkPeriods?.[0]?.dutyId) ||
                                    dayAvailability.blockedPeriods.some(isOpenableDutyBlock)
                                      ? 'pointer'
                                      : 'default',
                                  '&:hover': {
                                    opacity:
                                      (dayAvailability.isRosterPlanWorkOnlyDay &&
                                        dayAvailability.rosterPlanWorkPeriods?.[0]?.dutyId) ||
                                      dayAvailability.blockedPeriods.some(isOpenableDutyBlock)
                                        ? 0.8
                                        : 1
                                  }
                                }}
                              >
                                {calendarDayKind(dayAvailability) === 'rosterWork' ? (
                                  <Chip
                                    label={ROSTER_WORK_CALENDAR.chipLabel}
                                    size="small"
                                    sx={{
                                      fontSize: '0.65rem',
                                      bgcolor: ROSTER_WORK_CALENDAR.bg,
                                      color: '#004d40',
                                      border: `1px solid ${ROSTER_WORK_CALENDAR.border}`,
                                      fontWeight: 600
                                    }}
                                  />
                                ) : dayAvailability.isFullyAvailable ? (
                                  <Chip label="Available" size="small" color="success" sx={{ fontSize: '0.65rem' }} />
                                ) : dayAvailability.isFullyUnavailable ? (
                                  <Chip label="Unavailable" size="small" color="error" sx={{ fontSize: '0.65rem' }} />
                                ) : (
                                  <>
                                    <Chip label="Partial" size="small" color="warning" sx={{ fontSize: '0.65rem' }} />
                                    <Typography variant="caption" display="block">
                                      {dayAvailability.availableSlots.length} slot(s)
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Traditional Calendar View */
        <Box>
          <Grid container spacing={2}>
            {calendarAvailability
              .filter(item => !selectedCrewFilter || item.crew.id === selectedCrewFilter)
              .map((item) => (
                <Grid item xs={12} md={6} lg={4} key={item.crew.id}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        <Typography variant="h6" component="span">
                          {item.crew.firstName} {item.crew.lastName}
                        </Typography>
                        <CrewClearanceChips crew={item.crew} size="small" />
                      </Stack>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                        {item.availability.map((dayAvail) => (
                          <Box
                            key={dayAvail.date}
                            onClick={() => {
                              const rpId = dayAvail.rosterPlanWorkPeriods?.[0]?.dutyId;
                              if (dayAvail.isRosterPlanWorkOnlyDay && rpId) {
                                handleViewDutyFromCalendar(rpId);
                                return;
                              }
                              const dutyBlock = dayAvail.blockedPeriods.find(isOpenableDutyBlock);
                              if (dutyBlock && dutyBlock.details.id) {
                                handleViewDutyFromCalendar(dutyBlock.details.id);
                              }
                            }}
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              border: `2px solid ${
                                calendarDayKind(dayAvail) === 'rosterWork'
                                  ? ROSTER_WORK_CALENDAR.border
                                  : dayAvail.isFullyAvailable
                                    ? '#4caf50'
                                    : dayAvail.isFullyUnavailable
                                      ? '#f44336'
                                      : '#ff9800'
                              }`,
                              backgroundColor:
                                calendarDayKind(dayAvail) === 'rosterWork'
                                  ? ROSTER_WORK_CALENDAR.bg
                                  : dayAvail.isFullyAvailable
                                    ? '#e8f5e9'
                                    : dayAvail.isFullyUnavailable
                                      ? '#ffebee'
                                      : '#fff3e0',
                              cursor:
                                (dayAvail.isRosterPlanWorkOnlyDay && dayAvail.rosterPlanWorkPeriods?.[0]?.dutyId) ||
                                dayAvail.blockedPeriods.some(isOpenableDutyBlock)
                                  ? 'pointer'
                                  : 'default',
                              '&:hover': {
                                opacity:
                                  (dayAvail.isRosterPlanWorkOnlyDay && dayAvail.rosterPlanWorkPeriods?.[0]?.dutyId) ||
                                  dayAvail.blockedPeriods.some(isOpenableDutyBlock)
                                    ? 0.8
                                    : 1
                              }
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight="bold">
                              {dayjs(dayAvail.date).format('DD MMM YYYY (ddd)')}
                            </Typography>
                            {calendarDayKind(dayAvail) === 'rosterWork' ? (
                              <Chip
                                label={ROSTER_WORK_CALENDAR.chipLabel}
                                size="small"
                                sx={{
                                  mt: 0.5,
                                  bgcolor: ROSTER_WORK_CALENDAR.bg,
                                  color: '#004d40',
                                  border: `1px solid ${ROSTER_WORK_CALENDAR.border}`,
                                  fontWeight: 600
                                }}
                              />
                            ) : dayAvail.isFullyAvailable ? (
                              <Chip label="Fully Available" size="small" color="success" sx={{ mt: 0.5 }} />
                            ) : dayAvail.isFullyUnavailable ? (
                              <Chip label="Fully Unavailable" size="small" color="error" sx={{ mt: 0.5 }} />
                            ) : (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" display="block" fontWeight="bold">
                                  Available Slots:
                                </Typography>
                                {dayAvail.availableSlots.map((slot, idx) => (
                                  <Chip
                                    key={idx}
                                    label={`${formatTime(slot.start)} - ${formatTime(slot.end)} (${slot.duration.toFixed(1)}h)`}
                                    size="small"
                                    color="success"
                                    sx={{ mt: 0.5, mr: 0.5 }}
                                  />
                                ))}
                                {dayAvail.blockedPeriods.length > 0 && (
                                  <>
                                    <Typography variant="caption" display="block" fontWeight="bold" sx={{ mt: 1 }}>
                                      Blocked:
                                    </Typography>
                                    {dayAvail.blockedPeriods.map((blocked, idx) => {
                                      const acLabel = blocked.details?.aircraftLabel
                                        || [blocked.details?.aircraftRegistration, blocked.details?.aircraftModel]
                                          .filter(Boolean)
                                          .join(' · ');
                                      const baseLabel = `${blocked.type}: ${formatTime(blocked.start)} - ${formatTime(blocked.end)}`;
                                      return (
                                      <Chip
                                        key={idx}
                                        label={acLabel ? `${baseLabel} · ${acLabel}` : baseLabel}
                                        size="small"
                                        color={blocked.type === 'DUTY' ? 'primary' : 'error'}
                                        sx={{ 
                                          mt: 0.5, 
                                          mr: 0.5,
                                          cursor: isOpenableDutyBlock(blocked) ? 'pointer' : 'default'
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (isOpenableDutyBlock(blocked)) {
                                            handleViewDutyFromCalendar(blocked.details.id);
                                          }
                                        }}
                                      />
                                      );
                                    })}
                                  </>
                                )}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </Box>
      )}

      {/* Legend */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Legend:</strong>
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1, alignItems: 'center' }}>
          <Chip label="Fully Available" color="success" size="small" />
          <Chip
            label="Rostered to work"
            size="small"
            sx={{
              bgcolor: ROSTER_WORK_CALENDAR.bg,
              color: '#004d40',
              border: `1px solid ${ROSTER_WORK_CALENDAR.border}`,
              fontWeight: 600
            }}
          />
          <Chip label="Partially Available" color="warning" size="small" />
          <Chip label="Fully Unavailable" color="error" size="small" />
          <Chip label="On Duty (assigned)" color="primary" size="small" />
          <Chip label="On Leave" color="error" size="small" />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          &quot;Rostered to work&quot; means a roster plan work block only (no leave/off/other duty blocking that day).
          Still assignable for charters; click the cell to open the plan duty.
        </Typography>
      </Box>
    </Paper>
  );
};

export default CrewDutyRosterCalendar;
